import { computeOrderTotals } from "@/features/shared";
import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AddCustomerFormDialog } from "../../customers";
import { GeneralInformationSection } from "./general-information-section";
import { GeneralProductSection } from "./general-product-section";
import { LensProductSection } from "./lens-product-section";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./sales-order-form-constants";
import { SelectSalesRequestSection } from "./select-sales-request-section";

export const SalesOrderForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
}) => {
  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { watch, setValue, handleSubmit, formState } = methods;

  const [isAddCustomerDialogOpen, setIsAddCustomerDialogOpen] = useState(false);

  const salesOrderProductVariants = watch("salesOrderProductVariants");
  const salesOrderLensProducts = watch("salesOrderLensProducts");

  // Disable buyer and seller when there are existing products
  const hasExistingProducts =
    salesOrderProductVariants.length > 0 || salesOrderLensProducts.length > 0;

  useEffect(() => {
    const salesOrderProductVariantTotalPrice = salesOrderProductVariants.reduce(
      (acc, { quantity, costPerUnit }) => acc + quantity * costPerUnit,
      0
    );
    const salesOrderLensProductTotalPrice = salesOrderLensProducts.reduce(
      (acc, { price }) => acc + price,
      0
    );
    setValue(
      "subtotal",
      salesOrderProductVariantTotalPrice + salesOrderLensProductTotalPrice
    );
  }, [salesOrderProductVariants, salesOrderLensProducts, setValue]);

  const onFormSubmit = async (formData) => {
    const { taxAmount, grandTotal } = computeOrderTotals(formData);

    const payload = {
      sellerId: formData.seller.id,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      buyerableId: formData.buyer?.id,
      buyerableType: formData.buyerableType,
      warehouseId: formData.warehouse.id,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      grandTotal,
      paymentTermId: formData.paymentTerm.id,
      taxable: formData.taxable,
      totalIncludesTax: formData.taxable && formData.totalIncludesTax,
      taxPercentage: formData.taxPercentage,
      taxAmount,
      salesOrderLensProducts: formData.salesOrderLensProducts.map(
        ({
          salesRequest: {
            id: salesRequestId,
            purchaseOrder: { note },
          },
        }) => ({
          salesRequestId,
          discount: 0,
          availability: "PRE_ORDER",
          note: note || null,
        })
      ),
      salesOrderProductVariants: formData.salesOrderProductVariants.map(
        ({ salesRequestProductVariantId, quantity, note }) => ({
          salesRequestProductVariantId,
          quantity,
          note,
          discount: 0,
        })
      ),
    };

    await onSubmit(payload);
  };

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={3}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection disableBuyerSeller={hasExistingProducts} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <SelectSalesRequestSection />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <GeneralProductSection />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <LensProductSection />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PriceSummarySection />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
      {isAddCustomerDialogOpen && (
        <AddCustomerFormDialog
          isOpen
          onClose={() => setIsAddCustomerDialogOpen(false)}
        />
      )}
    </FormProvider>
  );
};

SalesOrderForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
